import type {TFunction} from "i18next";
import {z} from "zod";

/** Details on PATCH: optional; when present, 10–5000 chars (server `config/citation.php`). */
const detailsField = z
  .string()
  .min(10, "Details must be at least 10 characters")
  .max(5000)
  .optional()
  .nullable();

/** Create / form: omit or empty, or 10–5000 chars when provided. */
const citationDetailsInput = z.string().refine(
  (s) => {
    const t = s.trim();
    return t.length === 0 || (t.length >= 10 && t.length <= 5000);
  },
  {message: "Details must be between 10 and 5000 characters when provided"},
);

const fileOrUploadedUrl = z.union([
  z.string().min(1),
  z.custom<File>((v) => v instanceof File),
]);

const plateExpiresOnField = z
  .string()
  .regex(/^\d{4}-\d{2}$/, "Expected YYYY-MM")
  .optional();

const towRefine = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) =>
  schema.refine((v) => !(v.bypass_tow && v.immediate_tow), {
    message: "bypass_tow and immediate_tow cannot both be true",
    path: ["bypass_tow"],
  });

export const createCitationSchema = towRefine(
  z
    .object({
      vehicle_id: z.number(),
      context_property_id: z.number(),
      citation_type_ids: z.array(z.number()).min(1, "Select at least one citation type"),
      household_id: z.number().optional().nullable(),
      plate_expires_on: plateExpiresOnField,
      details: citationDetailsInput,
      gps_coordinates: z
        .string()
        .min(1, "GPS coordinates are required")
        .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/, "Expected lat,lon"),
      private_notes: z.string().optional().nullable(),
      bypass_tow: z.boolean().optional(),
      immediate_tow: z.boolean().optional(),
      files: z.array(z.string().min(1)).optional(),
      stored_vehicle_id: z.number().optional(),
    })
    .strict(),
);

export const updateCitationSchema = towRefine(
  z
    .object({
      details: detailsField,
      citation_type_ids: z.array(z.number()).min(1).optional(),
      public_notes: z.string().optional().nullable(),
      private_notes: z.string().optional().nullable(),
      send_notification: z.boolean().optional(),
      bypass_tow: z.boolean().optional(),
      immediate_tow: z.boolean().optional(),
      files: z.array(z.string().min(1)).optional(),
    })
    .strict()
    .partial(),
);

export type CreateCitationSchema = z.infer<typeof createCitationSchema>;
export type UpdateCitationSchema = z.infer<typeof updateCitationSchema>;

/** Form-only shape (vehicle/property injected on submit). */
export function createCitationCreateFormSchema(t: TFunction<"citation">) {
  return z
    .object({
      citation_type_ids: z
        .array(z.number())
        .min(1, t("validation.citation_types_required")),
      details: citationDetailsInput,
      plate_expires_on: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .nullable()
        .optional(),
      files: z.array(fileOrUploadedUrl).default([]),
      private_notes: z.string().optional(),
      bypass_tow: z.boolean(),
      immediate_tow: z.boolean(),
    })
    .refine((v) => !(v.bypass_tow && v.immediate_tow), {
      message: "Cannot enable both bypass tow and immediate tow",
      path: ["bypass_tow"],
    });
}

export const citationCreateFormSchema = createCitationCreateFormSchema(
  ((key: string) => key) as TFunction<"citation">,
);

export type CitationCreateFormValues = z.infer<ReturnType<typeof createCitationCreateFormSchema>>;

/** Edit sheet: details optional without the create-time 10-char minimum (still capped for API safety). */
export function createCitationEditFormSchema(t: TFunction<"citation">) {
  return z
    .object({
      citation_type_ids: z
        .array(z.number())
        .min(1, t("validation.citation_types_required")),
      details: z.string().max(5000, "Details must be at most 5000 characters"),
      files: z.array(fileOrUploadedUrl).default([]),
      private_notes: z.string().optional(),
      send_notification: z.boolean().optional(),
      bypass_tow: z.boolean(),
      immediate_tow: z.boolean(),
    })
    .refine((v) => !(v.bypass_tow && v.immediate_tow), {
      message: "Cannot enable both bypass tow and immediate tow",
      path: ["bypass_tow"],
    });
}

export const citationEditFormSchema = createCitationEditFormSchema(
  ((key: string) => key) as TFunction<"citation">,
);

export type CitationEditFormValues = z.infer<ReturnType<typeof createCitationEditFormSchema>>;
